import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { KanbanSquare, Mail, MessageSquare, Pencil, Plus, Search, Trash2, User as UserIcon, UserPlus, X } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import projectsService from '../services/projectsService';

/**
 * Project Management — team projects with assigned tasks.
 *
 * Deliberately NOT the system `tasks` collection: that holds machine-generated
 * chores (gate provisioning pending, lease follow-up). A team board showing
 * those would bury the work people were actually given.
 *
 * Assignment notifies the assignee on their own channel, text by default.
 */

const STATUSES = [
  { key: 'todo', label: 'To do' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'blocked', label: 'Blocked' },
  { key: 'done', label: 'Done' },
];

const PRIORITY_STYLE = {
  urgent: 'bg-red-50 text-red-700 ring-red-200',
  high: 'bg-amber-50 text-amber-700 ring-amber-200',
  normal: 'bg-slate-50 text-slate-600 ring-slate-200',
  low: 'bg-slate-50 text-slate-400 ring-slate-200',
};

const Projects = () => {
  const { currentFacility } = useFacility();
  const facilityId = currentFacility?._id || currentFacility?.id || '';

  const [projects, setProjects] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newProject, setNewProject] = useState('');
  const [newTask, setNewTask] = useState({ title: '', assignee_member_id: '', due_date: '' });
  const [invite, setInvite] = useState({ contact: '', full_name: '' });
  const [inviting, setInviting] = useState(false);
  const [lookup, setLookup] = useState('');
  const [tab, setTab] = useState('board');
  const [importable, setImportable] = useState([]);
  // Edit drawers. `null` = closed; otherwise the row being edited.
  const [editTask, setEditTask] = useState(null);
  const [editMember, setEditMember] = useState(null);
  // Inline rename in the sidebar: {id, name}. A modal for one field is heavy.
  const [renaming, setRenaming] = useState(null);
  const [allTasks, setAllTasks] = useState([]);
  const [taskQuery, setTaskQuery] = useState('');
  const [taskStatus, setTaskStatus] = useState('');
  const [taskAssignee, setTaskAssignee] = useState('');

  const load = useCallback(async () => {
    if (!facilityId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await projectsService.list(facilityId);
      const rows = Array.isArray(data) ? data : [];
      setProjects(rows);
      setActiveId((prev) => prev || (rows[0] && rows[0]._id) || null);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load projects');
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  // Team members, for the assignee picker.
  const loadUsers = useCallback(() => {
    if (!facilityId) return;
    projectsService.listMembers(facilityId)
      .then(({ data }) => setUsers(Array.isArray(data) ? data : []))
      .catch(() => setUsers([]));
    // Facility logins not yet on the roster, offered for one-click import.
    projectsService.importableUsers(facilityId)
      .then(({ data }) => setImportable(Array.isArray(data) ? data : []))
      .catch(() => setImportable([]));
  }, [facilityId]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const loadTasks = useCallback(async () => {
    if (!facilityId || !activeId) { setTasks([]); return; }
    try {
      const { data } = await projectsService.listTasks(facilityId, activeId);
      setTasks(Array.isArray(data) ? data : []);
    } catch {
      setTasks([]);
    }
  }, [facilityId, activeId]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  // Facility-wide task lookup. Server-side filters where the DB can do it;
  // free-text stays client-side over the returned rows.
  const loadAllTasks = useCallback(async () => {
    if (!facilityId) return;
    try {
      const params = {};
      if (taskStatus) params.status = taskStatus;
      if (taskAssignee) params.assignee_member_id = taskAssignee;
      const { data } = await projectsService.allTasks(facilityId, params);
      setAllTasks(Array.isArray(data) ? data : []);
    } catch {
      setAllTasks([]);
    }
  }, [facilityId, taskStatus, taskAssignee]);

  useEffect(() => { loadAllTasks(); }, [loadAllTasks]);

  const addProject = async (e) => {
    e.preventDefault();
    if (!newProject.trim()) return;
    try {
      const { data } = await projectsService.create(facilityId, { name: newProject.trim() });
      setNewProject('');
      setActiveId(data._id);
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not create project');
    }
  };

  const addTask = async (e) => {
    e.preventDefault();
    if (!newTask.title.trim() || !activeId) return;
    try {
      await projectsService.createTask(facilityId, activeId, {
        project_id: activeId,
        title: newTask.title.trim(),
        assignee_member_id: newTask.assignee_member_id || null,
        due_date: newTask.due_date || null,
      });
      setNewTask({ title: '', assignee_member_id: '', due_date: '' });
      loadTasks();
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not create task');
    }
  };

  const moveTask = async (task, status) => {
    await projectsService.updateTask(facilityId, activeId, task._id, { status });
    loadTasks();
    load();
  };

  const removeTask = async (task) => {
    await projectsService.removeTask(facilityId, activeId, task._id);
    loadTasks();
    load();
  };

  // Delete from the facility-wide list. Uses the task's OWN project_id —
  // activeId is whatever the board happens to have selected, which is
  // usually a different project and would 404 (or worse, hit the wrong one).
  const removeTaskFromList = async (task) => {
    if (!window.confirm(`Delete "${task.title}"?`)) return;
    try {
      await projectsService.removeTask(facilityId, task.project_id, task._id);
      loadAllTasks();
      load();
      if (task.project_id === activeId) loadTasks();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not delete task');
    }
  };

  // Save a task edit. Uses the task's OWN project_id so this works
  // identically from the board and the facility-wide list.
  const saveTask = async (e) => {
    e.preventDefault();
    const t = editTask;
    if (!t?.title?.trim()) return;
    try {
      await projectsService.updateTask(facilityId, t.project_id, t._id, {
        title: t.title.trim(),
        description: t.description || null,
        status: t.status,
        priority: t.priority,
        assignee_member_id: t.assignee_member_id || null,
        due_date: t.due_date || null,
      });
      setEditTask(null);
      loadTasks(); loadAllTasks(); load();
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not save the task');
    }
  };

  const saveMember = async (e) => {
    e.preventDefault();
    const m = editMember;
    try {
      await projectsService.updateMember(facilityId, m._id, {
        full_name: m.full_name || null,
        phone: m.phone || null,
        email: m.email || null,
        title: m.title || null,
        notify_channel: m.notify_channel,
        is_active: m.is_active,
      });
      setEditMember(null);
      loadUsers();
      setError(null);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(
        typeof d === 'string' ? d
          : Array.isArray(d) ? (d[0]?.msg || 'Check the details')
            : 'Could not save',
      );
    }
  };

  const saveProjectName = async (e) => {
    e.preventDefault();
    const name = (renaming?.name || '').trim();
    if (!name) return;
    try {
      await projectsService.update(facilityId, renaming.id, { name });
      setRenaming(null);
      load();
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not rename the project');
    }
  };

  const removeProject = async (project) => {
    // Deleting a project takes its tasks with it, so say how many.
    const n = project.task_count ?? 0;
    const warn = n
      ? `Delete "${project.name}" and its ${n} task${n === 1 ? '' : 's'}?`
      : `Delete "${project.name}"?`;
    if (!window.confirm(warn)) return;
    try {
      await projectsService.remove(facilityId, project._id);
      if (project._id === activeId) setActiveId(null);
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not delete project');
    }
  };

  // Create a roster person, then put them on this project. One field for
  // phone or email — an "@" tells us which, so asking would be busywork.
  // Phone is the norm: these people are texted, not emailed.
  const addMember = async (e) => {
    e.preventDefault();
    const contact = invite.contact.trim();
    if (!contact) return;
    const isEmail = contact.includes('@');
    setInviting(true);
    try {
      const { data } = await projectsService.createMember(facilityId, {
        phone: isEmail ? null : contact,
        email: isEmail ? contact : null,
        full_name: invite.full_name.trim() || null,
      });
      // Adding from the board also puts them on the open project; adding from
      // People just creates the person.
      if (tab === 'board' && activeId) {
        await projectsService.assignMember(facilityId, activeId, data._id);
      }
      setInvite({ contact: '', full_name: '' });
      loadUsers();
      load();
      setError(null);
    } catch (err) {
      // Surface what the server said. A 422 arrives as an array of field
      // errors, which the old typeof check discarded — so a real, actionable
      // reason (duplicate contact, bad phone) showed as "Could not add
      // member" and looked like a broken feature.
      const d = err?.response?.data?.detail;
      setError(
        typeof d === 'string' ? d
          : Array.isArray(d) ? (d[0]?.msg || 'Check the email or phone number')
            : 'Could not add member',
      );
    } finally {
      setInviting(false);
    }
  };

  // Add someone who already exists — no invite, no new account.
  const addExisting = async (u) => {
    const id = u._id || u.id;
    if (memberIds.includes(id)) return;
    try {
      await projectsService.assignMember(facilityId, activeId, id);
      load();
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not add member');
    }
  };

  // Remove someone from the facility roster entirely. The server refuses
  // while they hold open work rather than orphaning the assignee. Rather than
  // dead-ending on that 409, offer the two real ways forward: unassign the
  // work, or deactivate them and keep the history.
  const removeFromRoster = async (u) => {
    const id = u._id || u.id;
    const who = u.full_name || u.email || u.phone;
    const open = allTasksFor(id);

    if (open > 0) {
      const unassign = window.confirm(
        `${who} still has ${open} open task${open === 1 ? '' : 's'}.\n\n` +
        'OK — unassign those tasks and delete them from the roster.\n' +
        'Cancel — keep the tasks and deactivate them instead ' +
        '(they stay on past work but drop off the assignee list).',
      );
      try {
        if (unassign) {
          const mine = allTasks.filter(
            (t) => t.assignee_member_id === id && t.status !== 'done');
          for (const t of mine) {
            await projectsService.updateTask(
              facilityId, t.project_id, t._id, { assignee_member_id: null });
          }
          await projectsService.deleteMember(facilityId, id);
        } else {
          await projectsService.updateMember(facilityId, id, { is_active: false });
        }
        loadUsers(); loadAllTasks(); loadTasks(); load();
        setError(null);
      } catch (err) {
        setError(err?.response?.data?.detail || 'Could not remove them');
      }
      return;
    }

    if (!window.confirm(`Remove ${who} from this facility's roster?`)) return;
    try {
      await projectsService.deleteMember(facilityId, id);
      loadUsers();
      load();
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not remove them');
    }
  };

  // Copy a facility login onto the roster. Their account is untouched — this
  // creates a roster row that owns its own contact details from here on.
  const importUser = async (u) => {
    try {
      await projectsService.importUser(facilityId, u.user_id);
      loadUsers();
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not add them');
    }
  };

  const dropMember = async (userId) => {
    try {
      const { data } = await projectsService.removeMember(facilityId, activeId, userId);
      if (data?.open_tasks_still_assigned) {
        setError(`Removed from the project. ${data.open_tasks_still_assigned} open task(s) are still assigned to them.`);
      }
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not remove member');
    }
  };

  const userById = (id) => users.find((x) => (x._id || x.id) === id);
  const userName = (id) => {
    const u = userById(id);
    return u ? (u.full_name || u.email || u.phone) : null;
  };

  const active = projects.find((p) => p._id === activeId);
  const memberIds = active?.member_ids || [];
  // Assignment is limited to project members: assigning someone who isn't on
  // the project means they can't see the board the task lives on.
  const assignable = users.filter((u) => memberIds.includes(u._id || u.id));

  const q = lookup.trim().toLowerCase();
  const foundUsers = users.filter((u) => {
    if (!q) return true;
    return [u.full_name, u.email, u.phone]
      .some((f) => (f || '').toLowerCase().includes(q));
  });

  const tq = taskQuery.trim().toLowerCase();
  const filteredTasks = allTasks.filter((t) => {
    if (!tq) return true;
    return [t.title, t.description, t.project_name]
      .some((f) => (f || '').toLowerCase().includes(tq));
  });

  // Open tasks per person, for the People tab.
  const allTasksFor = (userId) => allTasks.filter(
    (t) => t.assignee_member_id === userId && t.status !== 'done').length;

  // What a person will ACTUALLY be sent: text silently falls back to email
  // when no phone is on file, so showing the raw preference would mislead.
  const effectiveChannel = (u) => {
    const ch = u?.notify_channel || 'text';
    if (ch === 'text' && !u?.phone) return u?.email ? 'email' : 'none';
    return ch;
  };

  return (
    <div className="p-6 max-w-[1400px]">
      <div className="flex items-center gap-2 mb-1">
        <KanbanSquare className="h-5 w-5 text-slate-500" />
        <h1 className="text-xl font-semibold text-slate-900">AI Project Management</h1>
      </div>
      <p className="text-[13px] text-slate-500 mb-5">
        Team projects and assigned tasks. Assignees are notified on their own
        channel — text by default.
      </p>

      {error && <p className="text-[13px] text-red-500 mb-3">{error}</p>}

      {/* Board is the working view; Tasks and People are lookups — finding a
          task shouldn't require knowing its project first. */}
      <div className="flex gap-1 border-b border-slate-200 mb-4">
        {[
          { key: 'board', label: 'Board' },
          { key: 'tasks', label: 'All tasks' },
          { key: 'people', label: 'People' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-[13px] -mb-px border-b-2 transition-colors ${
              tab === t.key
                ? 'border-blue-600 text-blue-700 font-medium'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
            {t.key === 'tasks' && (
              <span className="ml-1 text-[11px] text-slate-400">
                ({filteredTasks.length})
              </span>
            )}
            {t.key === 'people' && (
              <span className="ml-1 text-[11px] text-slate-400">
                ({foundUsers.length})
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'tasks' && (
        <div>
          <div className="flex flex-wrap gap-2 mb-3">
            <input
              value={taskQuery}
              onChange={(e) => setTaskQuery(e.target.value)}
              placeholder="Search tasks"
              className="flex-1 min-w-[200px] rounded-lg border border-slate-200 px-3 py-1.5 text-[13px]"
            />
            <select
              value={taskStatus}
              onChange={(e) => setTaskStatus(e.target.value)}
              className="rounded-lg border border-slate-200 px-2 py-1.5 text-[13px]"
            >
              <option value="">Any status</option>
              {STATUSES.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
            <select
              value={taskAssignee}
              onChange={(e) => setTaskAssignee(e.target.value)}
              className="rounded-lg border border-slate-200 px-2 py-1.5 text-[13px]"
            >
              <option value="">Anyone</option>
              {users.map((u) => (
                <option key={u._id || u.id} value={u._id || u.id}>
                  {u.full_name || u.email || u.phone}
                </option>
              ))}
            </select>
          </div>

          {filteredTasks.length === 0 ? (
            <p className="text-[13px] text-slate-400">
              {allTasks.length === 0
                ? 'No tasks yet at this facility.'
                : 'No tasks match those filters.'}
            </p>
          ) : (
            <div className="rounded-xl border border-slate-200/80 divide-y divide-slate-200/70">
              {filteredTasks.map((t) => (
                <div key={t._id} className="flex items-center gap-3 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <button
                      onClick={() => setEditTask({ ...t })}
                      className="text-[13px] text-slate-800 truncate block text-left hover:text-blue-700 w-full"
                    >
                      {t.title}
                    </button>
                    <p className="text-[11px] text-slate-400 truncate">
                      {t.project_name || 'Unknown project'}
                      {t.due_date && ` · due ${String(t.due_date).slice(0, 10)}`}
                    </p>
                  </div>
                  <span className="shrink-0 w-28 text-[12px] text-slate-600 truncate text-right">
                    {t.assignee_member_id
                      ? (userName(t.assignee_member_id) || 'Assigned')
                      : <span className="text-slate-300">Unassigned</span>}
                  </span>
                  <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] ring-1 ${
                    PRIORITY_STYLE[t.priority] || PRIORITY_STYLE.normal}`}>
                    {t.priority || 'normal'}
                  </span>
                  <span className="shrink-0 text-[11px] text-slate-500 w-20 text-right">
                    {STATUSES.find((s) => s.key === t.status)?.label || t.status}
                  </span>
                  <button
                    onClick={() => removeTaskFromList(t)}
                    title="Delete task"
                    className="shrink-0 text-slate-300 hover:text-red-500"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'people' && (
        <div>
          {/* The roster is managed here. These are the people who do the
              work — phone first, since they are texted their tasks. */}
          <form onSubmit={addMember} className="flex flex-wrap gap-2 mb-3">
            <input
              value={invite.contact}
              onChange={(e) => setInvite({ ...invite, contact: e.target.value })}
              placeholder="Phone number (or email)"
              className="flex-1 min-w-[200px] rounded-lg border border-slate-200 px-3 py-1.5 text-[13px]"
            />
            <input
              value={invite.full_name}
              onChange={(e) => setInvite({ ...invite, full_name: e.target.value })}
              placeholder="Name"
              className="w-[160px] rounded-lg border border-slate-200 px-3 py-1.5 text-[13px]"
            />
            <button
              type="submit"
              disabled={inviting}
              className="px-3 rounded-lg bg-blue-600 text-white text-[13px] disabled:opacity-50"
            >
              {inviting ? 'Adding…' : 'Add person'}
            </button>
          </form>

          <input
            value={lookup}
            onChange={(e) => setLookup(e.target.value)}
            placeholder="Search name, email or phone"
            className="w-full max-w-md rounded-lg border border-slate-200 px-3 py-1.5 text-[13px] mb-3"
          />
          {foundUsers.length === 0 ? (
            <p className="text-[13px] text-slate-400">
              No one matches “{lookup}”.
            </p>
          ) : (
            <div className="rounded-xl border border-slate-200/80 divide-y divide-slate-200/70">
              {foundUsers.map((u) => {
                const id = u._id || u.id;
                const open = allTasksFor(id);
                return (
                  <div key={id} className="flex items-center gap-3 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] text-slate-800 truncate">
                        {u.full_name || u.email || u.phone}
                        {u.is_active === false && (
                          <span className="ml-1.5 text-[10px] text-amber-600">invited</span>
                        )}
                      </p>
                      <p className="text-[11px] text-slate-400 truncate">
                        {[u.email, u.phone].filter(Boolean).join(' · ') || 'No contact details'}
                      </p>
                    </div>
                    <span className="shrink-0 text-[11px] text-slate-400">
                      {/* What they'll actually receive, not the raw preference. */}
                      {effectiveChannel(u) === 'none'
                        ? 'unreachable'
                        : effectiveChannel(u)}
                    </span>
                    <span className="shrink-0 text-[11px] text-slate-500 w-16 text-right">
                      {open > 0 ? `${open} open` : ''}
                    </span>
                    <button
                      onClick={() => setEditMember({ ...u })}
                      title="Edit person"
                      className="shrink-0 text-slate-300 hover:text-blue-600"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => removeFromRoster(u)}
                      title="Remove from roster"
                      className="shrink-0 text-slate-300 hover:text-red-500"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {importable.length > 0 && (
            <div className="mt-4 rounded-xl border border-slate-200/80 bg-slate-50/50 p-3">
              <p className="text-[12px] font-semibold text-slate-600 mb-1">
                Facility users
              </p>
              <p className="text-[11px] text-slate-400 mb-2">
                Operator logins at this facility. They aren't assignable until
                you add them here — most facility staff don't do project work.
              </p>
              <ul className="divide-y divide-slate-200/70">
                {importable.map((u) => (
                  <li key={u.user_id} className="flex items-center gap-3 py-1.5">
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] text-slate-800 truncate">
                        {u.full_name || u.email}
                        {u.role && (
                          <span className="ml-1.5 text-[10px] text-slate-400">
                            {String(u.role).replace(/_/g, ' ')}
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-slate-400 truncate">
                        {[u.email, u.phone].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <button
                      onClick={() => importUser(u)}
                      className="shrink-0 text-[12px] text-blue-600 hover:text-blue-700"
                    >
                      Add to roster
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-[11px] text-slate-400 mt-3">
            These are the people who do the work — they are texted their tasks
            and don't sign into the software. Operator logins are separate, in{' '}
            <Link to="/settings/users" className="text-blue-600 hover:underline">
              Settings → Users
            </Link>.
          </p>
        </div>
      )}

      <div className={`grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5 ${
        tab === 'board' ? '' : 'hidden'}`}>
        <div>
          <form onSubmit={addProject} className="flex gap-2 mb-3">
            <input
              value={newProject}
              onChange={(e) => setNewProject(e.target.value)}
              placeholder="New project"
              className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-[13px]"
            />
            <button type="submit" className="px-2 rounded-lg bg-blue-600 text-white">
              <Plus className="h-4 w-4" />
            </button>
          </form>

          {loading ? (
            <p className="text-[13px] text-slate-400">Loading…</p>
          ) : projects.length === 0 ? (
            <p className="text-[13px] text-slate-400">
              No projects yet. Create one to start assigning work.
            </p>
          ) : (
            <ul className="space-y-1">
              {projects.map((p) => (
                <li key={p._id} className="group flex items-stretch gap-1">
                  {renaming?.id === p._id ? (
                    <form onSubmit={saveProjectName} className="flex-1 flex gap-1">
                      <input
                        value={renaming.name}
                        onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
                        onBlur={saveProjectName}
                        onKeyDown={(e) => { if (e.key === 'Escape') setRenaming(null); }}
                        className="flex-1 min-w-0 rounded-lg border border-blue-300 px-2 py-1.5 text-[13px]"
                        autoFocus
                      />
                    </form>
                  ) : (
                  <button
                    onClick={() => setActiveId(p._id)}
                    onDoubleClick={() => setRenaming({ id: p._id, name: p.name })}
                    className={`flex-1 text-left px-3 py-2 rounded-lg text-[13px] transition-colors ${
                      p._id === activeId
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {p.name}
                    <span className="block text-[11px] text-slate-400">
                      {p.open_count ?? 0} open / {p.task_count ?? 0}
                    </span>
                  </button>
                  )}
                  <button
                    onClick={() => setRenaming({ id: p._id, name: p.name })}
                    title="Rename project"
                    className="px-1.5 rounded-lg text-slate-300 opacity-0 group-hover:opacity-100 hover:text-blue-600 hover:bg-blue-50 transition"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => removeProject(p)}
                    title="Delete project"
                    className="px-2 rounded-lg text-slate-300 opacity-0 group-hover:opacity-100 hover:text-red-500 hover:bg-red-50 transition"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          {!active ? (
            <p className="text-[13px] text-slate-400">Select or create a project.</p>
          ) : (
            <>
              {/* Members — who can be given work on this board. */}
              <div className="rounded-xl border border-slate-200/80 p-3 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <UserPlus className="h-3.5 w-3.5 text-slate-400" />
                  <p className="text-[12px] font-semibold text-slate-600">
                    Members
                  </p>
                </div>

                {memberIds.length === 0 ? (
                  <p className="text-[12px] text-slate-400 mb-2">
                    No members yet. Add someone by email or phone to assign them tasks.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {memberIds.map((id) => {
                      const u = userById(id);
                      const ch = effectiveChannel(u);
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 ring-1 ring-slate-200 pl-2 pr-1 py-0.5 text-[12px] text-slate-700"
                        >
                          {ch === 'email' ? (
                            <Mail className="h-3 w-3 text-slate-400" />
                          ) : ch === 'none' ? (
                            <UserIcon className="h-3 w-3 text-slate-300" />
                          ) : (
                            <MessageSquare className="h-3 w-3 text-slate-400" />
                          )}
                          {u ? (u.full_name || u.email || u.phone) : 'Unknown user'}
                          {u && u.is_active === false && (
                            <span className="text-[10px] text-amber-600">invited</span>
                          )}
                          <button
                            onClick={() => dropMember(id)}
                            title="Remove from project"
                            className="text-slate-300 hover:text-red-500"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Attach someone already on the roster. Creating people
                    lives in the People tab — this panel only decides who is
                    on THIS project. */}
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value=""
                    onChange={(e) => {
                      const u = users.find((x) => x._id === e.target.value);
                      if (u) addExisting(u);
                    }}
                    className="rounded-lg border border-slate-200 px-2 py-1.5 text-[13px]"
                  >
                    <option value="">Add someone…</option>
                    {users
                      .filter((u) => !memberIds.includes(u._id))
                      .map((u) => (
                        <option key={u._id} value={u._id}>
                          {u.full_name || u.email || u.phone}
                        </option>
                      ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setTab('people')}
                    className="text-[12px] text-blue-600 hover:text-blue-700"
                  >
                    Manage people
                  </button>
                </div>
              </div>

              <form onSubmit={addTask} className="flex flex-wrap gap-2 mb-4">
                <input
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder="New task"
                  className="flex-1 min-w-[200px] rounded-lg border border-slate-200 px-3 py-1.5 text-[13px]"
                />
                <select
                  value={newTask.assignee_member_id}
                  onChange={(e) => setNewTask({ ...newTask, assignee_member_id: e.target.value })}
                  className="rounded-lg border border-slate-200 px-2 py-1.5 text-[13px]"
                >
                  <option value="">Unassigned</option>
                  {assignable.map((u) => (
                    <option key={u._id || u.id} value={u._id || u.id}>
                      {u.full_name || u.email || u.phone}
                    </option>
                  ))}
                </select>
                <input
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                  className="rounded-lg border border-slate-200 px-2 py-1.5 text-[13px]"
                />
                <button type="submit" className="px-3 rounded-lg bg-blue-600 text-white text-[13px]">
                  Add
                </button>
              </form>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {STATUSES.map((col) => (
                  <div key={col.key} className="rounded-xl border border-slate-200/80 bg-slate-50/50 p-2">
                    <p className="text-[12px] font-semibold text-slate-600 px-1 pb-2">
                      {col.label}
                      <span className="text-slate-400 font-normal">
                        {' '}({tasks.filter((t) => t.status === col.key).length})
                      </span>
                    </p>
                    <div className="space-y-2">
                      {tasks.filter((t) => t.status === col.key).map((t) => (
                        <div key={t._id} className="rounded-lg bg-white border border-slate-200 p-2.5">
                          <div className="flex items-start justify-between gap-2">
                            <button
                              onClick={() => setEditTask({ ...t })}
                              className="text-[13px] text-slate-800 text-left hover:text-blue-700"
                            >
                              {t.title}
                            </button>
                            <button
                              onClick={() => removeTask(t)}
                              className="text-slate-300 hover:text-red-500"
                              title="Delete task"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] ring-1 ${
                              PRIORITY_STYLE[t.priority] || PRIORITY_STYLE.normal}`}>
                              {t.priority || 'normal'}
                            </span>
                            {t.assignee_member_id && (
                              <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                                <UserIcon className="h-3 w-3" />
                                {userName(t.assignee_member_id) || 'Assigned'}
                              </span>
                            )}
                            {t.due_date && (
                              <span className="text-[11px] text-slate-400">
                                due {String(t.due_date).slice(0, 10)}
                              </span>
                            )}
                          </div>
                          <select
                            value={t.status}
                            onChange={(e) => moveTask(t, e.target.value)}
                            className="mt-2 w-full rounded border border-slate-200 px-1.5 py-1 text-[11px] text-slate-600"
                          >
                            {STATUSES.map((s) => (
                              <option key={s.key} value={s.key}>{s.label}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Edit task */}
      {editTask && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/30 flex items-center justify-center p-4"
          onClick={() => setEditTask(null)}
        >
          <form
            onSubmit={saveTask}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-xl shadow-xl w-full max-w-md p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-[14px] font-semibold text-slate-800">Edit task</p>
              <button type="button" onClick={() => setEditTask(null)}
                className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>

            <label className="block text-[12px] text-slate-500 mb-1">Title</label>
            <input
              value={editTask.title || ''}
              onChange={(e) => setEditTask({ ...editTask, title: e.target.value })}
              className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-[13px] mb-3"
              autoFocus
            />

            <label className="block text-[12px] text-slate-500 mb-1">Details</label>
            <textarea
              rows={3}
              value={editTask.description || ''}
              onChange={(e) => setEditTask({ ...editTask, description: e.target.value })}
              className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-[13px] mb-3"
            />

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-[12px] text-slate-500 mb-1">Status</label>
                <select
                  value={editTask.status || 'todo'}
                  onChange={(e) => setEditTask({ ...editTask, status: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-[13px]"
                >
                  {STATUSES.map((st) => (
                    <option key={st.key} value={st.key}>{st.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[12px] text-slate-500 mb-1">Priority</label>
                <select
                  value={editTask.priority || 'normal'}
                  onChange={(e) => setEditTask({ ...editTask, priority: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-[13px]"
                >
                  {['low', 'normal', 'high', 'urgent'].map((pr) => (
                    <option key={pr} value={pr}>{pr}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-[12px] text-slate-500 mb-1">Assignee</label>
                <select
                  value={editTask.assignee_member_id || ''}
                  onChange={(e) => setEditTask({ ...editTask, assignee_member_id: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-[13px]"
                >
                  <option value="">Unassigned</option>
                  {users.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.full_name || u.email || u.phone}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[12px] text-slate-500 mb-1">Due</label>
                <input
                  type="date"
                  value={(editTask.due_date || '').slice(0, 10)}
                  onChange={(e) => setEditTask({ ...editTask, due_date: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-[13px]"
                />
              </div>
            </div>

            {/* Changing the assignee re-notifies; changing anything else does
                not, so say so rather than surprising someone. */}
            <p className="text-[11px] text-slate-400 mb-3">
              Assigning someone new sends them a text.
            </p>

            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setEditTask(null)}
                className="px-3 py-1.5 rounded-lg text-[13px] text-slate-600">
                Cancel
              </button>
              <button type="submit"
                className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-[13px]">
                Save
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit person */}
      {editMember && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/30 flex items-center justify-center p-4"
          onClick={() => setEditMember(null)}
        >
          <form
            onSubmit={saveMember}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-xl shadow-xl w-full max-w-md p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-[14px] font-semibold text-slate-800">Edit person</p>
              <button type="button" onClick={() => setEditMember(null)}
                className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>

            <label className="block text-[12px] text-slate-500 mb-1">Name</label>
            <input
              value={editMember.full_name || ''}
              onChange={(e) => setEditMember({ ...editMember, full_name: e.target.value })}
              className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-[13px] mb-3"
              autoFocus
            />

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-[12px] text-slate-500 mb-1">Phone</label>
                <input
                  value={editMember.phone || ''}
                  onChange={(e) => setEditMember({ ...editMember, phone: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-[13px]"
                />
              </div>
              <div>
                <label className="block text-[12px] text-slate-500 mb-1">Email</label>
                <input
                  value={editMember.email || ''}
                  onChange={(e) => setEditMember({ ...editMember, email: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-[13px]"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-[12px] text-slate-500 mb-1">Role</label>
                <input
                  value={editMember.title || ''}
                  onChange={(e) => setEditMember({ ...editMember, title: e.target.value })}
                  placeholder="e.g. Maintenance"
                  className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-[13px]"
                />
              </div>
              <div>
                <label className="block text-[12px] text-slate-500 mb-1">Notify by</label>
                <select
                  value={editMember.notify_channel || 'text'}
                  onChange={(e) => setEditMember({ ...editMember, notify_channel: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-[13px]"
                >
                  <option value="text">Text</option>
                  <option value="email">Email</option>
                  <option value="both">Text and email</option>
                  <option value="none">Nothing</option>
                </select>
              </div>
            </div>

            <label className="flex items-center gap-2 text-[13px] text-slate-700 mb-1">
              <input
                type="checkbox"
                checked={editMember.is_active !== false}
                onChange={(e) => setEditMember({ ...editMember, is_active: e.target.checked })}
              />
              Active
            </label>
            <p className="text-[11px] text-slate-400 mb-3">
              Inactive people keep their past work but drop off the assignee list.
            </p>

            {/* Text with no phone silently becomes email at send time, so say
                it here rather than letting it look like a working setup. */}
            {editMember.notify_channel === 'text' && !editMember.phone && (
              <p className="text-[11px] text-amber-600 mb-3">
                No phone number on file, so they will be emailed instead.
              </p>
            )}

            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setEditMember(null)}
                className="px-3 py-1.5 rounded-lg text-[13px] text-slate-600">
                Cancel
              </button>
              <button type="submit"
                className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-[13px]">
                Save
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Projects;
